import { useEffect, useState } from "react";
import { StepLoading } from "@/base/wizard/StepLoading";
import { ExtractedField } from "@/components/ExtractedReview";
import { NumericField } from "@/components/NumericField";
import { ReadOnlyField } from "@/components/ReadOnlyField";
import { SearchSelect } from "@/components/SearchSelect";
import { TextField } from "@/components/TextField";
import { FIELD_ERROR } from "@/components/fieldStyles";
import {
  HiddenAnswerErrors,
  QuestionField,
} from "@/components/submissions/wc/QuestionField";
import { StepLoadProblem } from "@/components/submissions/wc/StepLoadProblem";
import { TWO_COLUMNS } from "@/components/submissions/wc/grids";
import { RowPanel, SectionCard, YesNo, type WizardStepContext } from "@/base";
import { ConfirmDialog } from "@/base/ConfirmDialog";
import type { ManifestField } from "@/lib/brokerProducts";
import { localTodayIso } from "@/lib/dates";
import type { WcDraft } from "@/lib/submissions/wc/draft";
import {
  askedCoverageYears,
  businessAge,
  CLAIMS_QUESTION,
  CLAIMS_TOTAL,
  claimsInPast3Years,
  coverageYearCount,
  coverageYearTitle,
  CURRENT_CARRIER,
  noCoverageDefault,
  noCoverageOptions,
  NO_COVERAGE_REASON,
  PRIOR_COVERAGE_YEARS,
  resizeCoverageYears,
  yearsText,
  type WcCoverageYear,
} from "@/lib/submissions/wc/history";
import {
  historyFields,
  isAsked,
  shownAnswers,
  type WcAnswer,
  type WcAnswers,
} from "@/lib/submissions/wc/questions";
import { useWcManifest } from "@/lib/submissions/wc/useWcManifest";
import { usePriorCarriers } from "@/lib/usePriorCarriers";

// The WC wizard's Business › Characteristics & History (BRK-S389). The
// questions, their wording and their order are the manifest's `wc_history`
// section (PMS-S809), each drawn by its type: current coverage, the carrier
// or the reason, the years of prior coverage, then the claims. The rules are
// `history.ts`'s:
// - the reasons follow the business's age, from its start date and today,
//   and "New in business" is chosen for one under a month old;
// - the years answer lays out a card a year right under it, newest first,
//   dated from the desired effective date, each with its claims starting at
//   0; fewer years ask before cards go;
// - whether there were claims in the past 3 years, and how many, are worked
//   out from Years 1 to 3's and shown under the cards, never asked
//   (BRK-S505, PMS-S909; Nav, 2026-10-29);
// - years with the current carrier is PMS's sum, shown once it has one;
// - the carriers are read with the questions, and the step opens once both
//   are in, under one bar at its top (BRK-S488).

/** The question whose answer lays the cards out (PMS-S810). */
const isYears = (f: ManifestField) => f.key === PRIOR_COVERAGE_YEARS;

export function HistoryStep({
  draft,
  setDraft,
  setLoaded,
  errors,
  busy,
  extracted,
}: WizardStepContext<WcDraft>) {
  const { problem, retry } = useWcManifest(draft, setLoaded);
  const answers = draft.answers;
  const shown = shownAnswers(draft.manifest?.fields ?? [], answers);
  const fields = historyFields(draft.manifest);
  const count = askedCoverageYears(draft.manifest, answers);
  // The carriers are read with the questions, whatever the answers, so the
  // step opens whole under its one bar instead of filling in below its first
  // answer, and a Yes finds the list there (BRK-S488).
  const carriers = usePriorCarriers(true);
  // The carriers say which one is Other, which the checks and the save need:
  // a read, so never an unsaved change.
  useEffect(() => {
    const items = carriers.items;
    if (items) setLoaded((d) => ({ ...d, priorCarriers: items }));
  }, [carriers.items, setLoaded]);
  const [shrinking, setShrinking] = useState<string | null>(null);
  // Once open, the step stays open: a Retry of the carriers waits in place.
  const carriersSettled = carriers.items !== null || carriers.problem !== null;
  const [opened, setOpened] = useState(false);
  useEffect(() => {
    if (draft.manifest !== null && carriersSettled) setOpened(true);
  }, [draft.manifest, carriersSettled]);

  if (problem) return <StepLoadProblem problem={problem} onRetry={retry} />;
  if (draft.manifest === null || (!opened && !carriersSettled)) {
    return <StepLoading label="Loading the questions…" />;
  }
  if (fields.length === 0) {
    return (
      <p className="text-body text-brk-dim">
        Nothing to answer here for this application.
      </p>
    );
  }

  const age = businessAge(draft.business.businessStartDate, localTodayIso());
  const effectiveDate = draft.business.effectiveDate;

  const reasonField = fields.find((f) => f.key === NO_COVERAGE_REASON);

  /** The broker's answers. A reason that comes into view unanswered is "New
   *  in business" for a business under a month old; each year they now ask
   *  for gets a card, a new year 1 the current carrier's. Fewer years go
   *  through the question below. */
  const set = (patch: WcAnswers) => {
    for (const key of Object.keys(patch)) extracted.touch(key);
    setDraft((d) => {
      const next = { ...d.answers, ...patch };
      const reason = noCoverageDefault(age);
      if (
        reason &&
        reasonField &&
        next[NO_COVERAGE_REASON] === undefined &&
        isAsked(reasonField, shownAnswers(d.manifest?.fields ?? [], next))
      ) {
        next[NO_COVERAGE_REASON] = reason;
      }
      const want = askedCoverageYears(d.manifest, next);
      const carrier = next[CURRENT_CARRIER];
      return {
        ...d,
        answers: next,
        coverageYears:
          want !== undefined && d.coverageYears.length < want
            ? resizeCoverageYears(d.coverageYears, want, {
                effectiveDate,
                currentCarrierId:
                  typeof carrier === "string" ? carrier : undefined,
              })
            : d.coverageYears,
      };
    });
  };

  const answer = (f: ManifestField, value: WcAnswer) => {
    if (isYears(f)) {
      const want = coverageYearCount(value);
      if (want !== undefined && want < draft.coverageYears.length) {
        setShrinking(String(value));
        return;
      }
    }
    set({ [f.key]: value });
  };

  const setYear = (index: number, patch: Partial<WcCoverageYear>) =>
    setDraft((d) => ({
      ...d,
      coverageYears: d.coverageYears.map((y, i) =>
        i === index ? { ...y, ...patch } : y,
      ),
    }));

  const keep = coverageYearCount(shrinking ?? undefined) ?? 0;
  const going = draft.coverageYears.length - keep;

  return (
    <SectionCard>
      <div className="flex flex-col gap-5">
        {fields
          .filter((f) => isAsked(f, shown))
          .map((f) => (
            <div
              key={f.key}
              data-question={f.key}
              className="flex min-w-0 flex-col gap-3"
            >
              <ExtractedField marked={extracted.isPending(f.key)}>
                <QuestionField
                  field={
                    f.key === NO_COVERAGE_REASON
                      ? {
                          ...f,
                          options: noCoverageOptions(f.options ?? [], age),
                        }
                      : f
                  }
                  value={answers[f.key]}
                  onChange={(value) => answer(f, value)}
                  errors={errors}
                  busy={busy}
                  carriers={carriers}
                />
                <HiddenAnswerErrors
                  question={f}
                  fields={draft.manifest?.fields ?? []}
                  answers={shown}
                  errors={errors}
                />
              </ExtractedField>
              {f.key === CURRENT_CARRIER &&
                typeof draft.submission?.yearsWithCurrentCarrier ===
                  "number" && (
                  <dl className="sm:max-w-xs">
                    <ReadOnlyField
                      label="Years with this carrier"
                      value={yearsText(
                        draft.submission.yearsWithCurrentCarrier,
                      )}
                    />
                  </dl>
                )}
              {isYears(f) && count !== undefined && (
                <>
                  <CoverageYears
                    years={draft.coverageYears.slice(0, count)}
                    carriers={carriers}
                    errors={errors}
                    busy={busy}
                    onChange={setYear}
                  />
                  <ClaimsInPast3Years
                    years={draft.coverageYears.slice(0, count)}
                  />
                </>
              )}
            </div>
          ))}
      </div>
      <ConfirmDialog
        open={shrinking !== null}
        question={
          going === 1
            ? `Remove Year ${keep + 1} of prior coverage?`
            : `Remove Years ${keep + 1} to ${draft.coverageYears.length} of prior coverage?`
        }
        confirmLabel="Remove"
        cancelLabel={going === 1 ? "Keep it" : "Keep them"}
        onCancel={() => setShrinking(null)}
        onConfirm={() => {
          const value = shrinking;
          setShrinking(null);
          extracted.touch(PRIOR_COVERAGE_YEARS);
          setDraft((d) => ({
            ...d,
            answers: {
              ...d.answers,
              [PRIOR_COVERAGE_YEARS]: value ?? undefined,
            },
            coverageYears: d.coverageYears.slice(0, keep),
          }));
        }}
      />
    </SectionCard>
  );
}

/** A card a year, newest first: the carrier (Other with its name), the term
 *  and the premium. Errors are keyed as PMS keys a refused row. */
function CoverageYears({
  years,
  carriers,
  errors,
  busy,
  onChange,
}: {
  years: readonly WcCoverageYear[];
  carriers: ReturnType<typeof usePriorCarriers>;
  errors: Record<string, string>;
  busy: boolean;
  onChange: (index: number, patch: Partial<WcCoverageYear>) => void;
}) {
  if (carriers.problem) {
    return (
      <StepLoadProblem problem={carriers.problem} onRetry={carriers.retry} />
    );
  }
  const list = carriers.items;
  if (list === null) {
    return <StepLoading label="Loading the carriers…" framed={false} />;
  }
  const options = list.map((c) => ({ value: c.id, label: c.name }));
  return (
    <div className="flex flex-col gap-3">
      {errors.priorCoverage && (
        <p role="alert" className={FIELD_ERROR}>
          {errors.priorCoverage}
        </p>
      )}
      {years.map((y, i) => {
        const at = (field: string) => errors[`priorCoverage.${i}.${field}`];
        const other = list.some((c) => c.id === y.carrierId && c.isOther);
        return (
          <RowPanel key={y.key} title={coverageYearTitle(i)} headingLevel={3}>
            <div className={TWO_COLUMNS}>
              <SearchSelect
                label="Carrier"
                options={options}
                placeholder="Choose one"
                value={y.carrierId}
                onChange={(carrierId) =>
                  // A typed name belongs to Other alone.
                  onChange(i, { carrierId, carrierName: "" })
                }
                error={at("priorCarrierId")}
                disabled={busy}
                required
              />
              {other && (
                <TextField
                  label="Carrier name"
                  value={y.carrierName}
                  onChange={(e) => onChange(i, { carrierName: e.target.value })}
                  error={at("carrierName")}
                  disabled={busy}
                  required
                />
              )}
              <TextField
                label="Effective date"
                type="date"
                value={y.effectiveDate}
                onChange={(e) => onChange(i, { effectiveDate: e.target.value })}
                error={at("effectiveDate")}
                disabled={busy}
                required
              />
              <TextField
                label="Expiration date"
                type="date"
                value={y.expirationDate}
                onChange={(e) =>
                  onChange(i, { expirationDate: e.target.value })
                }
                error={at("expirationDate")}
                disabled={busy}
                required
              />
              <NumericField
                kind="currency"
                label="Premium"
                value={y.premium}
                onChange={(premium) => onChange(i, { premium })}
                error={at("totalPremium")}
                min={0}
                required
              />
              <NumericField
                kind="number"
                label="Claims"
                value={y.claimCount}
                onChange={(claimCount) => onChange(i, { claimCount })}
                error={at("claimCount")}
                min={0}
              />
            </div>
          </RowPanel>
        );
      })}
    </div>
  );
}

/** Whether there were claims in the past 3 years, chosen for the broker and
 *  locked, and after a Yes how many, beside it: worked out from Years 1 to
 *  3's as the broker types them, as PMS works them out (PMS-S909), never
 *  asked (Nav, 2026-10-29). */
function ClaimsInPast3Years({ years }: { years: readonly WcCoverageYear[] }) {
  const total = claimsInPast3Years(years) ?? 0;
  return (
    <div className={TWO_COLUMNS}>
      <YesNo label={CLAIMS_QUESTION} value={total > 0} readOnly />
      {total > 0 && (
        <dl>
          <ReadOnlyField label={CLAIMS_TOTAL} value={String(total)} />
        </dl>
      )}
    </div>
  );
}
